import * as _ from 'lodash';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { Cesium } from 'app/cesium';

export const IMS_TYPE__KML_FEATURE_LAYER = 'kmlFeatureLayer';

const SELECTED_ALPHA = 0.55;

interface IKMLFeatureLayer {
    entity: Cesium.Entity;
    siteId: number;
}

export class KMLFeatureLayer {
    private readonly _entity: Cesium.Entity;
    private readonly _siteId: number;
    private readonly _originalColor: Cesium.Color;
    private readonly _selectionType: string;
    private readonly _imsType: string;
    private readonly _isDeleted: boolean;

    constructor(resource: IKMLFeatureLayer) {
        this._entity = resource.entity;
        this._siteId = resource.siteId;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this._originalColor = this.graphics ? this.graphics?.material?.color?._value.clone() : null;
        this._selectionType = SelectionType.KML_FEATURE_LAYER;
        this._imsType = IMS_TYPE__KML_FEATURE_LAYER;
        this._isDeleted = false;
    }

    get id(): string {
        return this._entity.id;
    }

    get name(): string {
        return this._entity.name;
    }

    getEntity(): Cesium.Entity {
        return this._entity;
    }

    get shortName(): string {
        return _.truncate(this.name, { length: 25 });
    }

    get description(): string {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this._entity.description && this._entity.description._value
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              this._entity.description._value.replace(/<[^>]*>?/gm, '')
            : null;
    }

    get kmlType(): string {
        return this._entity.position ? 'KML Point Marker' : 'KML Feature';
    }

    get selectionType(): string {
        return this._selectionType;
    }

    get imsType(): string {
        return this._imsType;
    }

    get isDeleted(): boolean {
        return this._isDeleted;
    }

    get siteId(): number {
        return this._siteId;
    }

    get cartesian3s(): Cesium.PolygonHierarchy {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this._entity.polygon ? this._entity.polygon.hierarchy._value : null;
    }

    get graphics():
        | Cesium.PolylineGraphics
        | Cesium.PolygonGraphics
        | Cesium.RectangleGraphics
        | null {
        if (this._entity.polygon) {
            return this._entity.polygon;
        } else if (this._entity.polyline) {
            return this._entity.polyline;
        } else if (this._entity.rectangle) {
            return this._entity.rectangle;
        } else {
            return null;
        }
    }

    get originalColor(): Cesium.Color {
        return this._originalColor;
    }

    setSelected(): void {
        if (this.graphics) {
            this.makeColorProperty(this._entity, this.originalColor?.withAlpha(SELECTED_ALPHA));
        }
    }

    setDeselected(): void {
        if (this.graphics) {
            this.makeColorProperty(this._entity, this.originalColor);
        }
    }

    makeColorProperty(entity: Cesium.Entity, color: Cesium.Color): void {
        if (this.graphics) {
            if (this.graphics.material) {
                if (this.graphics.material instanceof Cesium.ImageMaterialProperty) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    this.graphics.material.color._value.alpha = color.alpha;
                } else {
                    const colorProperty = new Cesium.CallbackProperty(function (time, result) {
                        return color.clone(result);
                    }, false);
                    this.graphics.material = new Cesium.ColorMaterialProperty(colorProperty);
                }
            }
        }
    }
}
